import { DEFAULT_ERROR_MESSAGE, useFormContext, useFormShowContext } from 'framework/form';
import { ListItem, useListContext } from '../../ListContext';
import Add from '@mui/icons-material/Add';
import Undo from '@mui/icons-material/Undo';
import MinusIcon from '../../../ui/mui-builder/AppIcon/Icons/MinusIcon';
import { useEffect, useState } from 'react';
import ListLoadingDecorator from '../ListLoadingDecorator';
import { UiThemeOverrider, useUiTheme } from 'framework/uiTheme';
import ListFieldLayout from '../../../ui/layout/ListFieldLayout/ListFieldLayout';
import { Box, Grid2 as Grid, Stack, Typography, type Grid2Props } from '@mui/material';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { useListFormShowContext } from './EditableListFormShowContext';
import { get } from 'lodash';
import { GridCellInfo, NEW_ROW_ID_PREFIX } from './types';
import { ShowContextProvider, ShowData } from 'framework/show';
import Cell from './Cell';
import Alert from '../../../ui/mui-builder/Alert/Alert';
import { Button } from '../../../ui/mui-builder/Button/Button';

interface AlternativeListDataGridLayoutProps {
  disabledAddNewRow?: boolean;
  addNewText?: string;
  getTableCells: (isEditMode: boolean) => Promise<GridCellInfo<any>[]>;
  rowKeySource?: string;
  rowProps?: Grid2Props | ((data: any) => Grid2Props);
  getRowEnabled?: (data: any) => boolean;
  disabledRemove?: boolean | ((data: any) => boolean);
  getRowContentEnabled?: (data: any) => boolean;
  newRowIdPrefix?: string;
  newRowDefaultData?: Dictionary<any>;
  rerenderTableCellsDeps?: any[];
  customEnptyMessage?: string;
}

const EditableListDataGridLayout = ({
  getTableCells,
  rowProps,
  getRowEnabled,
  getRowContentEnabled,
  disabledAddNewRow = false,
  rowKeySource = 'id',
  newRowIdPrefix = NEW_ROW_ID_PREFIX,
  newRowDefaultData = {},
  rerenderTableCellsDeps = [],
  customEnptyMessage = 'No records found',
  addNewText = 'Add item',
  disabledRemove = false,
}: AlternativeListDataGridLayoutProps) => {
  const { addMessage } = useMessageService();
  const { Alert: ThemedAlert = Alert, Button: ThemedButton = Button } = useUiTheme();

  const { isEditMode } = useFormShowContext();
  const { formSource } = useListFormShowContext();
  const { data: formData } = useFormContext();

  const {
    data: { items = [] } = {},
    loading,
    addToList,
    markForDeletion,
    undoMarkForDeletion,
    deleteFromList,
    filter,
    sort,
  } = useListContext();

  const [isCellLoading, setIsCellLoading] = useState<boolean>(false);
  const [activeCells, setActiveCells] = useState<GridCellInfo<any>[]>([]);

  const emptyListComponent = () => {
    return <ThemedAlert color="info">{customEnptyMessage}</ThemedAlert>;
  };

  const tableRowComponent = (item: ListItem, rowIndex: number) => {
    const dataInstance = new ShowData(item);
    const isRowEnabled = getRowEnabled?.(dataInstance) || false;
    const isMarkedForDeletion = item?.['_isMarkedForDeletion'] === true;
    const isFiltered = item?.['_isFiltered'] === true;
    const isNewRow = item?.['_isNewRow'] === true;

    const isRowContentEnabled = isMarkedForDeletion ? false : getRowContentEnabled?.(dataInstance) || isRowEnabled;

    const isDisabledRemove = typeof disabledRemove === 'function' ? disabledRemove(dataInstance) : disabledRemove;

    const getRowProps = () => {
      if (!rowProps) return {};
      return typeof rowProps === 'function' ? rowProps(dataInstance) : rowProps;
    };

    const handleDeleteClick = () => {
      const rowId = item?.[rowKeySource];

      if (rowId?.toString()?.startsWith(newRowIdPrefix) || isNewRow) {
        deleteFromList?.(rowId);
      } else {
        markForDeletion?.(rowId);
      }
    };

    const handleUndoClick = () => {
      undoMarkForDeletion?.(item?.[rowKeySource]);
    };

    if (isFiltered && !isNewRow) {
      return null;
    }

    return (
      <ShowContextProvider
        value={{
          data: dataInstance,
        }}
      >
        <Grid
          spacing={0.5}
          alignItems="flex-start"
          justifyContent="space-between"
          padding={1}
          px={2}
          {...getRowProps()}
          key={dataInstance.getValueBySource(rowKeySource)}
          container
          sx={{
            ...getRowProps().sx,
            ...(isMarkedForDeletion && {
              backgroundColor: 'var(--mui-palette-action-hover)',
            }),
          }}
        >
          {isMarkedForDeletion && (
            <Grid size={12} display="flex" justifyContent="flex-end" mt={-1} mb={1} gap={1.5}>
              <Typography variant="caption" color="textSecondary">
                Will be deleted upon saving
              </Typography>

              <Grid size={0.3} display="flex" alignItems="center" justifyContent="flex-end" />
            </Grid>
          )}
          {activeCells?.map((cell, cellIndex) => (
            <Cell cellInfo={cell} rowIndex={rowIndex} cellIndex={cellIndex} isRowEnabled={isRowContentEnabled} />
          ))}
          {isEditMode && (
            <Grid size={0.3} display="flex" alignItems="center" justifyContent="flex-end">
              {isMarkedForDeletion ? (
                <ThemedButton
                  aria-label="undo"
                  variant="contained"
                  size="small"
                  sx={{ padding: '0px', minWidth: 0, mt: 1 }}
                  onClick={handleUndoClick}
                >
                  <Undo sx={{ color: 'common.white', fontSize: '1.5rem' }} />
                </ThemedButton>
              ) : isRowEnabled && !isDisabledRemove ? (
                <ThemedButton
                  aria-label="delete"
                  variant="contained"
                  sx={{ padding: '0px', minWidth: 0, mt: 1 }}
                  onClick={handleDeleteClick}
                >
                  <MinusIcon sx={{ color: 'common.white' }} />
                </ThemedButton>
              ) : null}
            </Grid>
          )}
        </Grid>
      </ShowContextProvider>
    );
  };

  const tableBodyComponent = () => {
    const editableData = get(formData, formSource);
    const isFilterEmpty = Object.keys(filter || {}).length === 0;
    const isSortApplied = !!sort?.field && !!sort?.order;

    const visibleItems = items.filter((item: ListItem) => !item?.['_isFiltered'] || item?.['_isNewRow'] === true);

    if (!items.length || visibleItems.length === 0) {
      return emptyListComponent();
    }

    const rows = items?.map((item: ListItem, index: number) => {
      const fixedIndex =
        isEditMode && (!isFilterEmpty || isSortApplied)
          ? editableData.findIndex((i: any) => i?.[rowKeySource] === item?.[rowKeySource])
          : index;
      return tableRowComponent(item, fixedIndex);
    });

    return <Stack direction="column">{rows}</Stack>;
  };

  useEffect(() => {
    const loadCellsData = async () => {
      setIsCellLoading(true);
      try {
        const loaded = await getTableCells(isEditMode);
        setActiveCells(loaded);
      } catch (e) {
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      } finally {
        setIsCellLoading(false);
      }
    };

    loadCellsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, ...rerenderTableCellsDeps]);

  return (
    <ListLoadingDecorator items={items && activeCells} loading={isCellLoading || loading}>
      <UiThemeOverrider
        overriddenLayouts={{
          BaseFieldLayout: ListFieldLayout,
        }}
      >
        {tableBodyComponent()}
        {isEditMode && !disabledAddNewRow && (
          <Box m={2} mt={1}>
            <ThemedButton
              variant="outlined"
              size="small"
              onClick={() =>
                addToList?.({
                  ...newRowDefaultData,
                  [rowKeySource]: `${newRowIdPrefix}${items.length + 1}`,
                  _isNewRow: true,
                })
              }
            >
              <Add sx={{ fontSize: '0.8rem' }} />
              {addNewText}
            </ThemedButton>
          </Box>
        )}
      </UiThemeOverrider>
    </ListLoadingDecorator>
  );
};

export default EditableListDataGridLayout;
